import React from 'react';
import { Hero } from '../components/marketing/Hero';
import { ValueProps } from '../components/marketing/ValueProps';
import { FeaturedCourses } from '../components/marketing/FeaturedCourses';
import { CTASection } from '../components/marketing/CTASection';
import { Outcomes } from '../components/marketing/Outcomes';
import { Instructor } from '../components/marketing/Instructor';
import { Testimonials } from '../components/marketing/Testimonials';
import { WarmHero } from '../components/marketing/warm/Hero';
import { WarmBelief } from '../components/marketing/warm/Belief';
import { WarmSteps } from '../components/marketing/warm/Steps';
import { WarmTestimonials } from '../components/marketing/warm/Testimonials';
import { WarmCTASection } from '../components/marketing/warm/CTASection';
import { UtilityHero } from '../components/marketing/utility/Hero';
import { UtilityMetrics } from '../components/marketing/utility/Metrics';
import { UtilityMatrix } from '../components/marketing/utility/Matrix';
import { UtilityPricing } from '../components/marketing/utility/Pricing';
import { UtilityFaq } from '../components/marketing/utility/Faq';
import { UtilityCTASection } from '../components/marketing/utility/CTASection';
import { ReviseHero } from '../components/marketing/revise/Hero';
import {
  ReviseLabStudy,
  ReviseLabEssay,
  ReviseLabPractice,
} from '../components/marketing/revise/LabSection';
import { ReviseReportGallery } from '../components/marketing/revise/ReportGallery';
import { ReviseGifts } from '../components/marketing/revise/Gifts';
import { RevisePricing } from '../components/marketing/revise/Pricing';
import { ReviseBrowseWindow, ReviseBundleWindow } from '../components/marketing/revise/BrowseWindow';
import { ReviseCredits } from '../components/marketing/revise/Credits';
import { ReviseCTASection } from '../components/marketing/revise/CTASection';
import { META } from '../components/marketing/revise/content';
import { useDocumentMeta } from '../lib/useDocumentMeta';
import {
  useTenant,
  useMarketingTheme,
  DEFAULT_MARKETING_THEME,
  getThemeDefaultSections,
} from '../context/TenantContext';
import type { MarketingSectionId, MarketingThemeId } from '../context/TenantContext';


type Renderers = Partial<Record<MarketingSectionId, React.FC>>;

const RENDERERS: Record<MarketingThemeId, Renderers> = {
  editorial: {
    hero: Hero,
    outcomes: Outcomes,
    valueProps: ValueProps,
    courses: FeaturedCourses,
    instructor: Instructor,
    testimonials: Testimonials,
    cta: CTASection,
  },
  warm: {
    hero: WarmHero,
    belief: WarmBelief,
    steps: WarmSteps,
    valueProps: ValueProps,
    courses: FeaturedCourses,
    instructor: Instructor,
    testimonials: WarmTestimonials,
    cta: WarmCTASection,
  },
  utility: {
    hero: UtilityHero,
    metrics: UtilityMetrics,
    matrix: UtilityMatrix,
    pricing: UtilityPricing,
    faq: UtilityFaq,
    valueProps: ValueProps,
    courses: FeaturedCourses,
    instructor: Instructor,
    testimonials: Testimonials,
    cta: UtilityCTASection,
  },
  // Bespoke, single-tenant (see THEME_TENANT_ALLOWLIST). Every entry is its own
  // component rather than a restyle of a shared one, so this theme has no
  // fallback into the editorial set — an ID it doesn't list simply doesn't
  // render, which is the correct outcome for e.g. `testimonials` here.
  revise: {
    hero: ReviseHero,
    labStudy: ReviseLabStudy,
    labEssay: ReviseLabEssay,
    outcomes: ReviseReportGallery,
    labPractice: ReviseLabPractice,
    bundles: ReviseBundleWindow,
    courses: ReviseBrowseWindow,
    gifts: ReviseGifts,
    pricing: RevisePricing,
    credits: ReviseCredits,
    cta: ReviseCTASection,
  },
};

/**
 * Themes whose sections must NOT fall back to the editorial renderers.
 *
 * The fallback exists so a theme that hasn't styled, say, `testimonials` still
 * shows the content instead of dropping it. That only works while the themes
 * share a visual vocabulary. `revise` is a self-contained dark design with its
 * own components, so an editorial block dropped into it renders as a white
 * panel mid-page — worse than not rendering at all.
 */
const NO_FALLBACK: ReadonlySet<MarketingThemeId> = new Set<MarketingThemeId>(['revise']);

export const LandingPage: React.FC = () => {
  const { tenant } = useTenant();
  const theme = useMarketingTheme();
  const sections = tenant?.branding.marketing?.sections ?? getThemeDefaultSections(theme);
  const themeRenderers = RENDERERS[theme] ?? RENDERERS[DEFAULT_MARKETING_THEME];
  const fallback = NO_FALLBACK.has(theme) ? {} : RENDERERS[DEFAULT_MARKETING_THEME];

  // The revise theme ships its own page title and description; every other
  // theme leaves the document meta to the shell.
  useDocumentMeta(theme === 'revise' ? { title: META.title, description: META.description } : {});

  return (
    <>
      {sections.map((id, i) => {
        const Component = themeRenderers[id] ?? fallback[id];
        if (!Component) return null;
        return <Component key={`${id}-${i}`} />;
      })}
    </>
  );
};
